import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Tabs } from 'antd';

import { MainTemplate } from 'templates';
import { KpiCard, KpiGrid } from 'molecules';
import { useGroupsOverview, useCommsRefresh } from 'core/hooks';

import { TaskBoard } from '../_detailShared/TaskBoard';
import { CommunicationsSection } from './CommunicationsSection';
import { GroupsTable } from './GroupsTable';
import * as S from './AccountManagerDashboard.styled';

type TDashboardTab = 'tasks' | 'comms' | 'groups';
const TAB_KEYS: TDashboardTab[] = ['tasks', 'comms', 'groups'];

/**
 * Account-manager command center (spec 0003 rework): KPI tiles + ONE global
 * search / group filter on top, three tabs below — Задачи (the 5-column task
 * board), Общение (conversations browser), Группы (sortable roster). Tab
 * state lives in `?tab=` (deep-linkable, survives refresh); KPI tiles are
 * clickable shortcuts into the matching tab.
 */
export const AccountManagerDashboard = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const tab: TDashboardTab = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('tab');
    return TAB_KEYS.includes(raw as TDashboardTab)
      ? (raw as TDashboardTab)
      : 'tasks';
  }, [location.search]);

  const setTab = useCallback(
    (next: string) => {
      const params = new URLSearchParams(location.search);
      if (next === 'tasks') params.delete('tab');
      else params.set('tab', next);
      history.replace({ ...location, search: params.toString() });
    },
    [history, location],
  );

  // Global filters — one search + one group select feeding every tab.
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);

  // Filtered rollup → KPI totals + the groups tab; a second, unfiltered call
  // (separate cache entry) feeds the pickers so typing a search never shrinks
  // the group dropdown or the task create form.
  const { groups, loading } = useGroupsOverview(search, onlyIssues);
  const { groups: pickerGroups } = useGroupsOverview();

  const totals = useMemo(
    () =>
      groups.reduce(
        (acc, g) => ({
          debtors: acc.debtors + g.debtorsCount,
          unanswered: acc.unanswered + g.unansweredCount,
          openTasks: acc.openTasks + g.openTasksCount,
          overdueTasks: acc.overdueTasks + g.overdueTasksCount,
        }),
        { debtors: 0, unanswered: 0, openTasks: 0, overdueTasks: 0 },
      ),
    [groups],
  );

  const visibleGroups = useMemo(
    () => (groupId ? groups.filter((g) => g.groupId === groupId) : groups),
    [groups, groupId],
  );

  // Dashboard refresh = everything: every group's Slack workspace + all
  // shared mailboxes (per-group refresh lives on the group card).
  const { refresh: refreshComms, syncing } = useCommsRefresh();

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.HeaderRow>
          <S.PageTitle>{t('pages.dashboard.title')}</S.PageTitle>
          <S.RefreshButton
            type="button"
            disabled={syncing}
            onClick={refreshComms}
          >
            {t('pages.communications.syncAll')}
          </S.RefreshButton>
        </S.HeaderRow>
        <S.SubTitle>{t('pages.dashboard.subtitle')}</S.SubTitle>

        <KpiGrid columns={4}>
          <KpiCard
            label={t('pages.dashboard.col.debtors')}
            value={totals.debtors}
            accent="#dc2626"
            onClick={() => setTab('groups')}
          />
          <KpiCard
            label={t('pages.dashboard.col.unanswered')}
            value={totals.unanswered}
            accent="#d97706"
            onClick={() => setTab('comms')}
          />
          <KpiCard
            label={t('pages.dashboard.kpiOpenTasks')}
            value={totals.openTasks}
            accent="#2563eb"
            onClick={() => setTab('tasks')}
          />
          <KpiCard
            label={t('pages.dashboard.kpiOverdueTasks')}
            value={totals.overdueTasks}
            accent="#dc2626"
            onClick={() => setTab('tasks')}
          />
        </KpiGrid>

        <S.FilterBar>
          <S.SearchInput
            type="search"
            value={search}
            placeholder={t('pages.dashboard.globalSearchPlaceholder')}
            onChange={(e) => setSearch(e.target.value)}
          />
          <S.Select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
          >
            <option value="">{t('pages.dashboard.allGroups')}</option>
            {pickerGroups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.name}
              </option>
            ))}
          </S.Select>
        </S.FilterBar>

        <Tabs activeKey={tab} onChange={setTab}>
          <Tabs.TabPane key="tasks" tab={t('pages.dashboard.tabs.tasks')}>
            <TaskBoard
              assigneeMe
              showCreateForm
              groups={pickerGroups}
              externalSearch={search}
              externalGroupId={groupId}
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="comms" tab={t('pages.dashboard.tabs.comms')}>
            <CommunicationsSection search={search} groupId={groupId} />
          </Tabs.TabPane>
          <Tabs.TabPane key="groups" tab={t('pages.dashboard.tabs.groups')}>
            <GroupsTable
              groups={visibleGroups}
              loading={loading}
              onlyIssues={onlyIssues}
              onOnlyIssues={setOnlyIssues}
            />
          </Tabs.TabPane>
        </Tabs>
      </S.Wrapper>
    </MainTemplate>
  );
});

AccountManagerDashboard.displayName = 'AccountManagerDashboard';
